import React from 'react';
import { List, ListItem, ListItemText, Typography } from '@mui/material';
import { ModalType } from 'entities/modal/model';
import { ECOSYSTEM_LINKS, Icons, ModalSidebar } from 'shared';

export interface RealEstateModalPayload {
  data?: null;
  type: ModalType.RealEstate;
  isOpen?: boolean;
}

export const RealEstateModal = React.forwardRef<
  HTMLElement,
  Pick<RealEstateModalPayload, 'data' | 'isOpen'> & { onClose: VoidFunction }
>(({ onClose, isOpen }, ref) => {
  return (
    <ModalSidebar
      isOpen={isOpen}
      ref={ref}
      onClose={onClose}
      title="Real Estate Trading Portal"
      subtitle="Property Ownership? Trade Smarter"
      icon={<Icons.RealEstateDark />}
      link={{
        title: 'Own Property with Confidence',
        link: {
          href: ECOSYSTEM_LINKS.useCases,
          text: `${ECOSYSTEM_LINKS.useCases}`,
          icon: '🔗',
        },
      }}
    >
      <Typography variant="inherit" color="inherit" fontFamily="inherit">
        The CratD2C Real Estate Trading Portal revolutionizes how land, homes, apartments, and commercial properties are
        bought and sold by bringing the entire transaction process onto the blockchain.
      </Typography>
      <br />
      <Typography variant="inherit" color="inherit" fontFamily="inherit">
        Every stage - listing, verification, due diligence, payment, and final ownership transfer - is handled through
        secure, automated smart contracts.
      </Typography>
      <br />
      <List
        disablePadding
        dense
        subheader={
          <Typography fontWeight="500" color="inherit" fontSize={18}>
            What Users Gain
          </Typography>
        }
      >
        <ListItem disableGutters>
          <ListItemText
            primary={
              <Typography fontWeight="500" color="inherit" fontSize="inherit">
                ✔ Zero ambiguity in ownership records
              </Typography>
            }
            secondary="All property titles and transaction histories are recorded immutably on the blockchain, eliminating disputes and paperwork inconsistencies."
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary={
              <Typography fontWeight="500" color="inherit" fontSize="inherit">
                ✔ Near-zero fraud risk
              </Typography>
            }
            secondary="Verification of sellers, assets, and documentation is automated and tamper-proof, preventing fake listings or double ownership."
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary={
              <Typography fontWeight="500" color="inherit" fontSize="inherit">
                ✔ Faster, automated transactions
              </Typography>
            }
            secondary="No delays from intermediaries or manual approval steps. Smart contracts handle everything instantly."
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary={
              <Typography fontWeight="500" color="inherit" fontSize="inherit">
                ✔ Full payment support via $CRAT
              </Typography>
            }
            secondary="Buy, sell, lease, or invest using the CratD2C native coin - enabling seamless settlement."
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary={
              <Typography fontWeight="500" color="inherit" fontSize="inherit">
                ✔ RWA (Real-World Asset) Tokenization
              </Typography>
            }
            secondary="Properties can be converted into fractional digital tokens, allowing multiple investors to co-own high-value assets with low entry requirements."
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary={
              <Typography fontWeight="500" color="inherit" fontSize="inherit">
                ✔ Global accessibility
              </Typography>
            }
            secondary="Anyone from anywhere can participate in property markets globally, thanks to borderless blockchain infrastructure."
          />
        </ListItem>
      </List>
      <br />
      <Typography fontWeight="500" color="inherit" fontSize={18} mb={2}>
        Why It Matters
      </Typography>
      <Typography variant="inherit" color="inherit" fontFamily="inherit">
        By merging real estate with decentralized technology, CratD2C removes inefficiencies, reduces costs, and opens
        real estate to global participation.
      </Typography>
      <br />
      <Typography variant="inherit" color="inherit" fontFamily="inherit">
        It makes property acquisition simpler, safer, faster, and more transparent than ever before.
      </Typography>
    </ModalSidebar>
  );
});
